/**
 * A lesson's start (ADR-032): the view is black until the world is ready,
 * then fades in over 1.5 s -- with water, ash and clouds already moving --
 * so the first thing a class sees is the place, not the place arriving. A
 * teleport keeps its own 0.3 s fade; where both run, the darker one wins.
 */

export const INTRO_SECONDS = 1.5;
/** Seconds of black before the view says why: a world can take up to 15 s. */
export const INTRO_HINT_AFTER = 2;
/** What it says, in Dutch. */
export const INTRO_HINT = 'De wereld wordt geladen…';

export class IntroFade {
  #state: 'clear' | 'black' | { t: number; seconds: number } = 'clear';
  #held = 0;
  #hintText: string = INTRO_HINT;

  /** Black, until `start`; after 2 s the black says `hint` (a 360° photo: "De foto wordt geladen…"). */
  hold(hint: string = INTRO_HINT): void {
    this.#state = 'black';
    this.#held = 0;
    this.#hintText = hint;
  }

  /** What the black says once `hint` is due. */
  get hintText(): string {
    return this.#hintText;
  }

  /** Fades in from black over `seconds`, eased at both ends. */
  start(seconds = INTRO_SECONDS): void {
    this.#state = { t: 0, seconds: Math.max(0.01, seconds) };
  }

  reset(): void {
    this.#state = 'clear';
    this.#held = 0;
  }

  /** Black for a while now: time to say the world is loading. */
  get hint(): boolean {
    return this.#state === 'black' && this.#held >= INTRO_HINT_AFTER;
  }

  /** Waiting, or fading in. */
  get active(): boolean {
    return this.#state !== 'clear';
  }

  /** Advances by `dt` seconds and returns the black's opacity, 0–1. */
  update(dt: number): number {
    const state = this.#state;
    if (state === 'clear') return 0;
    if (state === 'black') {
      this.#held += dt;
      return 1;
    }
    state.t += dt;
    const u = Math.min(1, state.t / state.seconds);
    if (u >= 1) {
      this.#state = 'clear';
      return 0;
    }
    return 1 - u * u * (3 - 2 * u);
  }
}
